/**
 * @file GeneSymbolField.js
 * @description
 *
 * @copyright veriome labs, llc.  - all rights reserved.
 * This file is subject to the terms and conditions defined in
 * file 'LICENSE.md', which is part of this source code package.
 * 
 * https://kyleshevlin.com/debounce-and-throttle-callbacks-with-react-hooks
 * 
 * @author don michael
 * @since Nov 23, 2021 11:31
 */
import axiosClient from 'src/services/axiosClient.js';
import debounce from 'lodash/debounce';

import { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';

import { Autocomplete } from '@mui/material';
import TextField from '@mui/material/TextField';

import { arrayExists, objectExists, stringExists } from 'src/@common/utilities';

const callApi = (searchTerm) => {

    return new Promise((resolve, reject) => {

        if (stringExists(searchTerm)) {

            const baseUrl = import.meta.env.VITE_APP_API_BASE_URL;

            const config = {
                method: 'post',
                baseURL: baseUrl,
                url: '/gene/geneSearch/geneSymbols',
                data: {
                    searchTerm,
                },
            };

            axiosClient(config)
                .then((response) => {
                    resolve(response.data.payload);
                })
                .catch((err) => {
                    reject(err);
                });

        } else {
            resolve();
        }

    });
}

function GeneSymbolField(props) {
    const {
        fieldName = 'geneSymbol',
        fieldLabel = 'Gene Symbol',
        currentValue,
        geneCurations,
        fieldErrors = {},
        helpText = '',
        isDisabled = false,
        isFocus = false,
        handleBlur = () => { },
        handleChange,
    } = props;

    const [helperText, setHelperText] = useState('');
    const [noOptionsText, setNoOptionsText] = useState('No gene symbols found');
    const [open, setOpen] = useState(false);
    const [options, setOptions] = useState([]);
    const [value, setValue] = useState('');
    const [loading, setLoading] = useState(false);

    const debouncedSearch = useMemo(
        () =>
            debounce((text) => {

                setNoOptionsText('Loading...');
                setLoading(true)
                setOptions([]);

                callApi(text)
                    .then((results) => {

                        const newOptions = [];

                        if (arrayExists(results)) {
                            results.forEach((item) => {
                                newOptions.push(item);
                            });
                        } else {
                            setNoOptionsText('No genes found');
                        }

                        setOptions(newOptions);
                        setLoading(false);

                    }).catch((err) => {
                        console.error(err);
                        setLoading(false);
                    });
            }, 700),
        []
    )

    useEffect(() => {
        // add any provided curations to the options
        if (arrayExists(geneCurations)) {
            const newOptions = [];

            geneCurations.forEach((geneSymbol) => {
                newOptions.push(geneSymbol);
            });

            // mui complains if the currentValue does not exist in
            // the options, so add it.
            if (stringExists(currentValue)) {
                if (!newOptions.includes(currentValue)) {
                    newOptions.push(currentValue);
                }
            }

            // mui also complains if the array does not contain
            // a blank string, so add it.
            if (!newOptions.includes('')) {
                newOptions.push('');
            }

            setOptions(newOptions.sort((a, b) => (a > b) ? 1 : -1));
        }

    }, [geneCurations, currentValue]);

    useEffect(() => {
        if (currentValue) {
            setValue(currentValue);
        }
    }, [currentValue]);

    useEffect(() => {
        // sets the mondoId into helperText
        if (objectExists(fieldErrors)) {
            if (fieldErrors[fieldName]) {
                setHelperText(fieldErrors[fieldName])
            } else {
                if (objectExists(value)) {
                    if (stringExists(helpText)) {
                        setHelperText(helpText);
                    } else {
                        setHelperText(value.id)
                    }
                } else {
                    if (stringExists(helpText)) {
                        setHelperText(helpText);
                    } else {
                        setHelperText('')
                    }
                }
            }
        }

    }, [fieldErrors, fieldName, helpText, value]);

    const onInputChange = (event, newValue, reason) => {
        // using this changeEvent to identify keystroke input,
        // indicating a needed call to the api to provide new options 

        let ucaseValue = newValue;

        switch (reason) {
            case 'clear':
                break;

            case 'reset':
                break;

            case 'input':

                // transformation: upperCase
                if (stringExists(newValue)) {
                    ucaseValue = newValue.toUpperCase();
                }

                // update local state and inform the parent of the change
                onChange(null, ucaseValue);

                // keystroke input.  hit the api
                debouncedSearch(ucaseValue, (apiOptions) => {

                    if (!arrayExists(apiOptions)) {
                        setOptions([])

                    } else {

                        const transformedOptions = [];

                        apiOptions.forEach((option) => {
                            transformedOptions.push(option);
                        });

                        setOptions(transformedOptions);
                    }
                });

                break;

            default:
                break;
        }
    };

    const onChange = (event, newValue) => {
        // this change event provides the newly selected object.
        // update both the local and parent state.

        // set the local state
        setValue(newValue);

        // wrap in a synthetic event
        const newEvent = {
            target: {
                type: 'autocomplete',
                value: newValue,
            }
        };

        // call the parent change handler
        handleChange(newEvent, 'geneSymbol');
    };

    return (
        <Autocomplete
            id={fieldName}
            name={fieldName}
            disablePortal={true}
            disableClearable={true}
            open={open}
            onOpen={() => {
                setOpen(true);
            }}
            onClose={() => {
                setOpen(false);
            }}
            autoSelect={false}
            getOptionLabel={(option) => option}
            filterOptions={(x) => x}  // must disable filtering on client
            includeInputInList
            blurOnSelect={false}
            onBlur={() => {
                handleBlur(fieldName);
            }}
            onInputChange={onInputChange}
            onChange={onChange}
            options={options}
            loading={loading}
            noOptionsText={noOptionsText}
            isOptionEqualToValue={() => {
                return true;
            }}
            value={value}
            sx={{
                width: 200,
                mr: '10px',
            }}
            renderInput={(params) => (
                <TextField
                    {...params}
                    label={fieldLabel}
                    size="small"
                    margin="dense"
                    variant="filled"
                    // error={Boolean(fieldErrors[fieldName])}
                    autoFocus={isFocus}
                    helperText={helperText}
                    disabled={isDisabled}
                />
            )}
        />
    );
}

GeneSymbolField.propTypes = {
    fieldLabel: PropTypes.string,
    fieldName: PropTypes.string,
    currentValue: PropTypes.string,
    geneCurations: PropTypes.array,
    fieldErrors: PropTypes.object,
    helpText: PropTypes.string,
    isDisabled: PropTypes.bool,
    isFocus: PropTypes.bool,
    handleBlur: PropTypes.func,
    handleChange: PropTypes.func.isRequired,
};

export default GeneSymbolField;
